import { all, takeEvery, fork, call, put } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

// action
import { wholesaleActions } from '@redux/store/index'

// api
import { wholesaleApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// schemas
import { Order, WholesaleCatalogType, WholesaleOrderReqType } from '@schemas'

/** 고객 토큰 — Api.connection 이 스태프 토큰을 먼저 집으므로 직접 넘긴다(customerOrder.saga 와 같다) */
const customerHeaders = (): { [key: string]: string } => {
	const token = sessionStorage.getItem('accessCustomerToken') ?? ''
	return token === '' ? {} : { Authorization: `Bearer ${token}` }
}

const problemText = (res: ResType | undefined, fallback: string): string => {
	const data: any = res?.data
	const problems: any[] = data?.message?.meta?.detail?.problems ?? data?.problems ?? []
	if (Array.isArray(problems) && problems.length > 0) {
		return problems.map(p => p.message).join(' · ')
	}
	const title = data?.message?.meta?.title
	return typeof title === 'string' ? title : data?.message?.errorMessage ?? fallback
}

function* fetchCatalog() {
	try {
		yield put(wholesaleActions.CATALOG_STATUS('loading'))
		const res: ResType | undefined = yield call(wholesaleApi.getWholesaleCatalog, {
			customHeaders: customerHeaders(),
		})
		if (res !== undefined && res.status === 200) {
			yield put(wholesaleActions.CATALOG(res.data as unknown as WholesaleCatalogType))
			yield put(wholesaleActions.CATALOG_STATUS('ok'))
		} else {
			yield put(wholesaleActions.CATALOG(null))
			yield put(wholesaleActions.CATALOG_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(wholesaleActions.CATALOG_STATUS('empty'))
	}
}

function* fetchOrders() {
	try {
		yield put(wholesaleActions.ORDER_LIST_STATUS('loading'))
		const res: ResType | undefined = yield call(wholesaleApi.getWholesaleOrders, {
			customHeaders: customerHeaders(),
		})
		if (res !== undefined && res.status === 200) {
			const list = (res.data as any[]).map(a => new Order(a))
			yield put(wholesaleActions.ORDER_LIST(list))
			yield put(wholesaleActions.ORDER_LIST_STATUS(list.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(wholesaleActions.ORDER_LIST([]))
			yield put(wholesaleActions.ORDER_LIST_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(wholesaleActions.ORDER_LIST_STATUS('empty'))
	}
}

function* initPage() {
	yield all([call(fetchCatalog), call(fetchOrders)])
}

function* placeOrder(action: PayloadAction<WholesaleOrderReqType>) {
	try {
		const res: ResType | undefined = yield call(wholesaleApi.createWholesaleOrder, {
			body: action.payload,
			customHeaders: customerHeaders(),
		})
		if (res !== undefined && res.status < 400) {
			const orderId: string = (res.data as any)?.orderId ?? ''
			yield put(
				wholesaleActions.PLACE_RESULT({
					ok: true,
					orderId,
					message: `Order ${orderId} placed. Stock is reserved. An invoice follows by email.`,
				}),
			)
		} else {
			yield put(
				wholesaleActions.PLACE_RESULT({
					ok: false,
					orderId: '',
					message: problemText(res, 'The order could not be placed.'),
				}),
			)
		}
		yield all([call(fetchCatalog), call(fetchOrders)])
	} catch (error) {
		Logger.warn(error)
		yield put(
			wholesaleActions.PLACE_RESULT({
				ok: false,
				orderId: '',
				message: 'The order could not be placed.',
			}),
		)
	} finally {
		yield put(wholesaleActions.IS_PLACING(false))
	}
}

function* mainSaga() {
	yield takeEvery(wholesaleActions.TRIGGER_INIT_WHOLESALE_PAGE, initPage)
	yield takeEvery(wholesaleActions.PLACE_ORDER, placeOrder)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
